import { appendIf } from '@btime/helpers'
import { assoc, compose, filter, map, path, pick, prop, propEq } from 'ramda'
import { gql } from '@apollo/client'
import React from 'react'

import { cleanDynamicValueV2 } from './dynamicInputs'
import { fieldsClient } from '../apollo/api'
import { hasValue } from './value'
import { useFormState } from '../../packages/kiti/components/form/form/FormHandler'
import { useQuery } from '../apollo/_helpers/useQuery'
import DynamicInput from '../../components/form/DynamicInput'

const QUERY = gql`
  query Fields($origin: [String], $type: [String]) {
    fields(filter: { origins: $origin, types: $type }, limit: 999999999, sort: { field: ORDINATION, type: ASC }) {
      id
      name
      origin
      ordination
      required
      type
      defaultValue
      reference
      parentReference
      displayConditions
      masks {
        id
        name
        pattern
      }
      configuration
      options {
        id
        name
        reference
        ordination
      }
    }
  }
`

export const useFieldsFromApi = (origin, type, options) => {
  // `fetchPolicy` opcional: o padrão segue 'cache-first' (como sempre); só o gatilho "resposta editada" passa outro.
  const { skip, fetchPolicy = 'cache-first' } = options || {}
  return useQuery(QUERY, {
    variables: { origin, type },
    skip,
    client: fieldsClient,
    fetchPolicy
  })
}

export const fetchFieldsFromApi = (origin, type) =>
  fieldsClient
    .query({
      query: QUERY,
      variables: { origin, type },
      fetchPolicy: 'network-only'
    })
    .then(res => res.data?.fields || [])

export const getFieldsFromCache = (origin, type) => {
  const data = fieldsClient.readQuery({
    query: QUERY,
    variables: { origin, type }
  })

  return data?.fields || []
}

export const cleanFieldValuesRequestClone = compose(
  map(
    compose(
      pick(['fieldId', 'value']), //
      item => assoc('value', cleanDynamicValueV2(item.value, item.field.type), item)
    )
  ),
  filter(compose(hasValue, prop('value')))
)

export const cleanFieldValuesRequest = compose(
  map(
    compose(
      pick(['fieldId', 'value', 'id']), //
      item => assoc('value', cleanDynamicValueV2(item.value, item.field.type), item)
    )
  ),
  filter(compose(hasValue, prop('value')))
)

export const validateFieldValues = (form, fieldValuesPath) => {
  fieldValuesPath = fieldValuesPath || ['fieldValues']
  const fieldValues = path(fieldValuesPath, form) || []
  return fieldValues.reduce((errors, fieldValue, index) => {
    const field = fieldValue?.field || {}
    return [
      ...errors,
      ...appendIf(!!field.required && !hasValue(fieldValue.value), {
        path: [...fieldValuesPath, index, 'value'],
        message: 'Campo obrigatório'
      })
    ]
  }, [])
}

export const useFieldsForBulkForm = (origin, fieldValuesPath) => {
  fieldValuesPath = fieldValuesPath || ['fieldValues']
  const { data, ...state } = useFieldsFromApi(origin)

  const fields = data?.fields?.map((field, index) => ({
    ...field,
    label: field.name + (field.required ? '*' : ''),
    path: [...fieldValuesPath, index],
    name: 'value',
    Input: DynamicInput,
    valueType: field.type,
    displayAsList: field.configuration?.displayAsList ?? undefined,
    width: 15
  }))

  return [fields || [], state]
}

export const formatFieldValues = (fieldValues, origin, isPropSnakeCase) => {
  const propFieldId = isPropSnakeCase ? 'field_id' : 'fieldId'

  const fields = getFieldsFromCache(origin)
  if (!fields?.length) return []

  return fields.map(field => ({
    field,
    [propFieldId]: field.id,
    value: fieldValues?.find(propEq(propFieldId, field.id))?.value
  }))
}

export const useInitialFieldValuesFormatter = (origin, fieldValuesPath, isPropSnakeCase = false) => {
  const propFieldValue = `${isPropSnakeCase ? 'field_values' : 'fieldValues'}`

  fieldValuesPath = fieldValuesPath || [propFieldValue]

  const { form, handleChangeIn } = useFormState()
  const fields = getFieldsFromCache(origin)

  React.useEffect(() => {
    const fieldValues = path(fieldValuesPath, form) || []
    handleChangeIn(fieldValuesPath, formatFieldValues(fieldValues, origin, isPropSnakeCase))
  }, [fields])
}
